import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import {
  isSameDay,
  isSameMonth,
  format,
  addMonths,
  subMonths,
  isBefore,
  setDate,
  setMonth,
  setYear,
} from 'date-fns';
import PropTypes from 'prop-types';

import getDateFNSLocale from 'common/utils/date/getDateFNSLocale';
import getWeekArray from 'common/utils/date/getWeekArray';
import R from 'common/res/R';

class Calendar extends React.Component {
  static propTypes = {
    date: PropTypes.object.isRequired,
    onDateChange: PropTypes.func,
    maximumDate: PropTypes.object,
    minimumDate: PropTypes.object,
    locale: PropTypes.string,
  };

  state = {
    currentMonth: this.props.date,
  };

  handleSelectDate = date => {
    if (this.props.onDateChange) {
      const newYear = setYear(this.props.date, date.getFullYear());
      const newMonth = setMonth(newYear, date.getMonth());
      const newDate = setDate(newMonth, date.getDate());

      this.props.onDateChange(newDate);
    }
  };

  handlePrev = () => {
    this.setState({ currentMonth: subMonths(this.state.currentMonth, 1) });
  };

  handleNext = () => {
    this.setState({ currentMonth: addMonths(this.state.currentMonth, 1) });
  };

  renderPrevButton = () => {
    const showPrevButton = this.props.minimumDate
      ? !isSameMonth(this.state.currentMonth, this.props.minimumDate)
      : true;

    return (
      <TouchableOpacity onPress={showPrevButton ? this.handlePrev : undefined}>
        {showPrevButton && <Text style={styles.chevron}>‹</Text>}
        {!showPrevButton && <Text style={styles.chevron}>&nbsp;</Text>}
      </TouchableOpacity>
    );
  };

  renderNextButton = () => {
    const showNextButton = this.props.maximumDate
      ? !isSameMonth(this.state.currentMonth, this.props.maximumDate)
      : true;

    return (
      <TouchableOpacity onPress={showNextButton ? this.handleNext : undefined}>
        {showNextButton && <Text style={styles.chevron}>›</Text>}
        {!showNextButton && <Text style={styles.chevron}>&nbsp;</Text>}
      </TouchableOpacity>
    );
  };

  renderMonthSelection = () => {
    return (
      <View style={styles.monthSelectionContainer}>
        {this.renderPrevButton()}
        <Text style={styles.month}>
          {format(this.state.currentMonth, 'MMMM YYYY', {
            locale: this.getLocale(),
          })}
        </Text>
        {this.renderNextButton()}
      </View>
    );
  };

  renderMonth = date => {
    return getWeekArray(date).map(this.renderWeek);
  };

  renderWeek = week => {
    return (
      <View key={week.toString()} style={styles.weekContainer}>
        {week.map(this.renderDay)}
      </View>
    );
  };

  renderDay = date => {
    const { maximumDate, minimumDate, date: selectedDate } = this.props;

    const currentShownMonth = this.state.currentMonth.getMonth();
    const dateInShownMonth = date.getMonth() === currentShownMonth;
    const dateIsSelectedDate = isSameDay(date, selectedDate);

    const dateWithinMaximumRange = maximumDate
      ? isBefore(date, maximumDate) || isSameDay(date, maximumDate)
      : true;
    const dateWithinMinimumRange = minimumDate
      ? isBefore(minimumDate, date) || isSameDay(minimumDate, date)
      : true;
    const dateWithinRange = dateWithinMaximumRange && dateWithinMinimumRange;

    const dateText = dateInShownMonth && date.getDate();
    const dateIsSelectable = dateWithinRange && dateInShownMonth;
    const handlePress = dateIsSelectable
      ? () => this.handleSelectDate(date)
      : undefined;

    const Wrapper = dateIsSelectable ? TouchableOpacity : View;

    return (
      <Wrapper
        style={styles.dateContainer}
        key={date.toString()}
        onPress={handlePress}
      >
        <Text
          style={[
            styles.date,
            dateIsSelectedDate && dateIsSelectable && styles.selectedDate,
            !dateIsSelectable && styles.disabledDate,
          ]}
        >
          {dateText}
        </Text>
      </Wrapper>
    );
  };

  renderDayText = (text, key) => (
    <View style={styles.dateContainer} key={key}>
      <Text style={styles.day}>{text}</Text>
    </View>
  );

  getLocale = () => getDateFNSLocale(this.props.locale);

  render() {
    const { date } = this.props;
    const locale = this.getLocale();

    return (
      <View style={styles.calendar}>
        <View style={styles.calendarHeader}>
          <Text style={styles.headerYearLabel}>
            {format(date, 'YYYY', { locale })}
          </Text>
          <Text style={styles.headerDateLabel} numberOfLines={1}>
            {format(date, 'dddd, D MMM', { locale })}
          </Text>
        </View>
        <View style={styles.calendarWrapper}>
          {this.renderMonthSelection()}

          <View style={styles.weekContainer}>
            {['S', 'S', 'R', 'K', 'J', 'S', 'M'].map((text, i) =>
              this.renderDayText(text, i)
            )}
          </View>

          {this.renderMonth(this.state.currentMonth)}
        </View>
      </View>
    );
  }
}

const CALENDAR_BORDER_RADIUS = 2;
const styles = StyleSheet.create({
  calendar: {
    maxWidth: 256,
    backgroundColor: R.color.uiLightPrimary,
    alignSelf: 'center',
    borderRadius: CALENDAR_BORDER_RADIUS,
  },
  calendarHeader: {
    borderTopLeftRadius: CALENDAR_BORDER_RADIUS,
    borderTopRightRadius: CALENDAR_BORDER_RADIUS,
    backgroundColor: R.color.brand,
    padding: R.spacing.m,
  },
  headerYearLabel: {
    ...R.typography.weightMedium,
    color: R.color.uiLightPrimary,
    fontSize: R.typography.fontSize.small,
    lineHeight: R.typography.fontSize.large,
    opacity: 0.7,
  },
  headerDateLabel: {
    ...R.typography.weightMedium,
    color: R.color.uiLightPrimary,
    fontSize: R.typography.fontSize.huge,
  },
  calendarWrapper: {
    padding: R.spacing.m,
  },
  monthSelectionContainer: {
    padding: R.spacing.xxs,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chevron: {
    ...R.typography.weightMedium,
    fontFamily: 'Arial',
    fontSize: R.typography.fontSize.large,
    paddingVertical: R.spacing.s,
    paddingHorizontal: R.spacing.xs,
  },
  month: {
    ...R.typography.weightMedium,
    fontSize: R.typography.fontSize.regular,
  },
  weekContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  dateContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  day: {
    padding: R.spacing.xs,
    borderRadius: R.border.radius.default,
    margin: R.spacing.xxs,
    textAlign: 'center',
    color: R.color.textSecondary,
  },
  date: {
    paddingVertical: R.spacing.xxs,
    borderRadius: R.border.radius.default,
    margin: R.spacing.xxs,
    fontSize: R.typography.fontSize.small,
    lineHeight: R.typography.fontSize.medium,
    textAlign: 'center',
  },
  selectedDate: {
    color: R.color.uiLightPrimary,
    backgroundColor: R.color.brand,
  },
  disabledDate: {
    color: R.color.textSecondary,
  },
});

export default Calendar;
